/**
 * cl-core/numbers — 같은 수치 문자열 반복 예산(§9 CL5 「같은 수치 문자열이 한 페이지 본문에 3회를 넘으면 실패」). 순수 TS.
 *
 * 토큰화는 게이트 러너(tools/cl/gates/gates.mjs numericTokens)와 같은 규칙이다:
 *   날짜(연·월·일·시각·D-day) 제거 → 「숫자(+쉼표·소수)+단위」 · 단위 없는 맨숫자는 쉼표가 있을 때만 · 두 자리 미만 제외.
 * 표 하나 안의 같은 값은 1회로 센다(러너 동일) — `takeTable` 을 쓴다.
 */

export const CL5_MAX = 3;
const UNITS = ['억원', '억', '만원', '원', '세대', '가구', '㎡', 'm²', '평', '%', '건', '곳', '개월', '개', '동', '층', '대', '명', '년', '주'];

const DATE_RES = [
  /\b(19|20)\d{2}\s?[.\-/년]\s?\d{1,2}\s?[.\-/월]?\s?(\d{1,2}\s?일?)?/g,
  /\d{1,2}\s?월\s?\d{1,2}\s?일/g,
  /\d{1,2}:\d{2}/g,
  /\b\d{1,2}\s?\/\s?\d{1,2}\b/g,
  /\b(19|20)\d{2}\s?년?/g,
  /D[-+]\d+/gi,
];

/** 텍스트 → 수치 토큰 목록(러너와 같은 표기: 「1,061세대」「8.9억」). */
export function numericTokens(text: string | null | undefined): string[] {
  let s = String(text ?? '');
  for (const re of DATE_RES) s = s.replace(new RegExp(re.source, re.flags), ' ');
  const out: string[] = [];
  const re = /(\d{1,3}(?:,\d{3})+|\d+)(?:\.(\d+))?\s?/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(s))) {
    const prev = m.index > 0 ? s.charAt(m.index - 1) : '';
    if (/[\d.,]/.test(prev)) continue;
    const rest = s.slice(m.index + m[0].length);
    let unit = '';
    for (const u of UNITS) if (rest.startsWith(u) && u.length > unit.length) unit = u;
    const digits = (m[1] + (m[2] || '')).replace(/,/g, '');
    if (!unit && m[1].indexOf(',') < 0) continue;
    if (digits.length < 2) continue;
    out.push(`${m[1]}${m[2] ? '.' + m[2] : ''}${unit}`);
  }
  return out;
}

/* search-guard (2026-10-22) · instr/cl/cl5_tokenizer_1530(#4h) — 게이트 «측정» 토크나이저: 「N억 M만(원)」 한 금액 = 한 토큰.
 *   종전 토큰화는 「10억 9,367만 · 10억 9,378만 · 10억 500만」(서로 다른 세 금액)을 「10억」×3 으로 세어 CL5 를 거짓 실패시켰다
 *   (CL5 「10억」 반복 · 두산위브더제니스-대연). 측정 보정이다(완화 아님) — 진짜 같은 금액 「10억 9,378만」×3 은 그대로 실패.
 *   표기: 「N억 M만」(M 은 쉼표 세 자리 · 「8천(만)」= 8,000만 · 「8000만」= 8,000만 · 뒤 「원」 생략). M ≥ 1만 이면 묶지 않는다.
 *   ⚠️ 렌더 예산(NumericBudget · body/render · ClDetail)은 종전 numericTokens 그대로 — 게이트 측정용과 렌더용을 나눈다(지시).
 *   러너(tools/cl/gates/gates.mjs numericTokens)와 같은 규칙 — cl5-repeat.test 가 같음을 잠근다. */
const EOK_MAN_RE = /(?<![\d.,])(\d{1,3}(?:,\d{3})+|\d+)\s?억\s?(\d{1,3}(?:,\d{3})+|\d{1,4})\s?(천\s?만|천|만)\s?(?:원)?/g;
const commas = (n: number) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

/** 게이트 측정용 수치 토큰 — numericTokens + 「N억 M만」 묶음(위 머리말). 순서 = 본문 등장 순. */
export function numericTokensGate(text: string | null | undefined): string[] {
  let s = String(text ?? '');
  for (const re of DATE_RES) s = s.replace(new RegExp(re.source, re.flags), ' ');
  const merged: Array<[number, string]> = [];
  s = s.replace(new RegExp(EOK_MAN_RE.source, EOK_MAN_RE.flags), (all: string, eok: string, man: string, unit: string, at: number) => {
    const m = Number(man.replace(/,/g, '')) * (unit.startsWith('천') ? 1000 : 1);
    if (!(m > 0 && m < 10000)) return all;
    merged.push([at, `${eok}억 ${commas(m)}만`]);
    return ' '.repeat(all.length);
  });
  const rest: Array<[number, string]> = [];
  const re = /(\d{1,3}(?:,\d{3})+|\d+)(?:\.(\d+))?\s?/g;
  let mm: RegExpExecArray | null;
  while ((mm = re.exec(s))) {
    const prev = mm.index > 0 ? s.charAt(mm.index - 1) : '';
    if (/[\d.,]/.test(prev)) continue;
    const after = s.slice(mm.index + mm[0].length);
    let unit = '';
    for (const u of UNITS) if (after.startsWith(u) && u.length > unit.length) unit = u;
    const digits = (mm[1] + (mm[2] || '')).replace(/,/g, '');
    if (!unit && mm[1].indexOf(',') < 0) continue;
    if (digits.length < 2) continue;
    rest.push([mm.index, `${mm[1]}${mm[2] ? '.' + mm[2] : ''}${unit}`]);
  }
  return [...merged, ...rest].sort((a, b) => a[0] - b[0]).map(([, t]) => t);
}

/** 페이지 단위 수치 예산. 넣기 전에 fits 로 묻고, 넣으면 take. */
export class NumericBudget {
  private count = new Map<string, number>();
  constructor(private max = CL5_MAX) {}
  /** 이 텍스트를 더해도 어떤 토큰도 max 를 넘지 않는가. */
  fits(text: string | null | undefined): boolean {
    const add = new Map<string, number>();
    for (const t of numericTokens(text)) add.set(t, (add.get(t) || 0) + 1);
    let ok = true;
    add.forEach((n, t) => { if ((this.count.get(t) || 0) + n > this.max) ok = false; });
    return ok;
  }
  take(text: string | null | undefined): void {
    for (const t of numericTokens(text)) this.count.set(t, (this.count.get(t) || 0) + 1);
  }
  /** 표 하나 — 같은 값은 1회. */
  takeTable(cells: Array<string | null | undefined>): void {
    const seen = new Set<string>();
    for (const c of cells) for (const t of numericTokens(c)) seen.add(t);
    seen.forEach((t) => this.count.set(t, (this.count.get(t) || 0) + 1));
  }
  fitsTable(cells: Array<string | null | undefined>): boolean {
    const seen = new Set<string>();
    for (const c of cells) for (const t of numericTokens(c)) seen.add(t);
    let ok = true;
    seen.forEach((t) => { if ((this.count.get(t) || 0) + 1 > this.max) ok = false; });
    return ok;
  }
  /** fits 면 take 하고 true. */
  tryTake(text: string | null | undefined): boolean {
    if (!this.fits(text)) return false;
    this.take(text);
    return true;
  }
  /** 현재 최다 토큰(점검용). */
  worst(): [string, number] | null {
    let best: [string, number] | null = null;
    this.count.forEach((n, t) => { if (!best || n > best[1]) best = [t, n]; });
    return best;
  }
}

/** 이름에서 수치 토큰을 걷은 «짧은 이름»(제목 반복용 · 「익산 부송에코르 10년 공공임대주택」→「익산 부송에코르 공공임대주택」). */
export function nameWithoutNumbers(name: string): string {
  const toks = numericTokens(name);
  let s = String(name ?? '');
  for (const t of toks) s = s.split(t).join(' ');
  return s.replace(/\s+/g, ' ').trim() || String(name ?? '').trim();
}
